import {gql} from '@apollo/client';
import {Alert, Center, Loader, Modal} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {AntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {
  SchemaFieldSelectorsForMigrationQuery,
  SchemaFieldSelectorsForMigrationQuery_schemaFieldSelectors_SchemaFieldSelectorNode as RegistrySchemaFieldSelector,
} from 'antibodies/schema-configuration/schema-migration/__generated/SchemaFieldSelectorsForMigrationQuery';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import MigrationPreviewBody from 'antibodies/schema-configuration/schema-migration/MigrationPreviewBody';
import {getActiveOptionMappingViewData} from 'antibodies/schema-configuration/schema-migration/newFieldsMigrationUtils';
import SourceFieldValueMappingView from 'antibodies/schema-configuration/schema-migration/SourceFieldValueMappingView';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

export const SCHEMA_FIELD_SELECTORS_FOR_MIGRATION_QUERY = gql`
  query SchemaFieldSelectorsForMigrationQuery @skipAudit @engComponent(name: SCHEMAS) {
    schemaFieldSelectors {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
        registry {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

export interface AntibodyComponentMigrationPreviewModalProps {
  componentTypeName: string;
  componentInterfaceId: AntibodyComponentSchemaInterfaceId;
  registryId: string;
  enforcedSchemaSpec: AntibodyEnforcedSchemaSpec;
  existingFields: Array<SchemaField | ClientSchemaField>;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  hasNameTemplate: boolean;
  folderItemType: string | null;
  onClose: () => void;
  onApply: (options: {
    namingStrategiesToAdd?: Array<NamingStrategy>;
    fieldMigrationMappings: Array<FieldMigrationMapping>;
  }) => void | Promise<void>;
  isApplying?: boolean;
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>;
}

/**
 * Confirmation modal shown before applying a protein component type to an existing schema.
 * Previews the enforced changes the migration will make and lets the user confirm or cancel.
 */
export function AntibodyComponentMigrationPreviewModal({
  componentTypeName,
  componentInterfaceId,
  registryId,
  enforcedSchemaSpec,
  existingFields,
  existingConstraint,
  existingNamingStrategies,
  hasNameTemplate,
  folderItemType,
  onClose,
  onApply,
  isApplying = false,
  allRegistrySelectors,
}: AntibodyComponentMigrationPreviewModalProps): JSX.Element {
  const [fieldMigrationMappings, setFieldMigrationMappings] = useState<Array<FieldMigrationMapping>>([]);
  // The destination field whose value-mapping sub-view is currently open, or null when showing the
  // main preview. Lifted here (not in NewFieldsMigrationPreview) so the sub-view can take over the
  // whole modal body and its footer.
  const [activeOptionMappingField, setActiveOptionMappingField] = useState<string | null>(null);

  const handleOptionMappingChange = useCallback(
    (destFieldName: string, sourceOptionId: string, destOptionName: string | null): void => {
      setFieldMigrationMappings((prev) =>
        prev.map((m) => {
          if (m.destinationFieldName !== destFieldName) {
            return m;
          }
          const current = m.optionValueMappings ?? [];
          const without = current.filter((ovm) => ovm.sourceOptionId !== sourceOptionId);
          // Both a concrete option and an explicit "— none —" (destOptionName === null) are recorded
          // as entries — the entry itself marks the option as acknowledged. Only a source option with
          // no entry at all is treated as implicitly unmapped (and flagged for review).
          return {
            ...m,
            optionValueMappings: [...without, {sourceOptionId, destinationOptionName: destOptionName}],
          };
        })
      );
    },
    []
  );

  // The field whose caret opened the sub-view, remembered so focus can return to that caret when the
  // user backs out. The caret unmounts while the sub-view is open, so it's re-queried by its (unique,
  // stable) aria-label once the preview re-mounts.
  const returnFocusFieldRef = useRef<string | null>(null);

  const handleOpenOptionMapping = useCallback((destFieldName: string): void => {
    returnFocusFieldRef.current = destFieldName;
    setActiveOptionMappingField(destFieldName);
  }, []);

  const handleCloseOptionMapping = useCallback((): void => {
    setActiveOptionMappingField(null);
  }, []);

  const activeMappingData = getActiveOptionMappingViewData(
    activeOptionMappingField,
    fieldMigrationMappings,
    existingFields,
    allRegistrySelectors,
    enforcedSchemaSpec.enforcedFieldSpecs,
    registryId
  );
  const showValueMappingView = activeMappingData != null && activeOptionMappingField != null;

  // Restore focus to the originating "Map options" caret after backing out of the sub-view. Switching
  // panes unmounts the control that had focus while the Modal itself stays mounted (so its focus trap
  // is not reinitialized) — without this, keyboard and screen-reader users are stranded on
  // document.body. The sub-view focuses its own Back button on open; this handles the return trip.
  useEffect(() => {
    if (!showValueMappingView && returnFocusFieldRef.current != null) {
      const fieldName = returnFocusFieldRef.current;
      returnFocusFieldRef.current = null;
      document.querySelector<HTMLButtonElement>(`[aria-label="Map options for ${fieldName}"]`)?.focus();
    }
  }, [showValueMappingView]);

  return (
    <Modal
      title={`Apply ${componentTypeName} protein component type`}
      opened={true}
      onClose={onClose}
      size="lg"
      padding="lg"
      closeOnClickOutside={!isApplying}
      closeOnEscape={!isApplying}
      withCloseButton={!isApplying}
      {...getTestProps({component: 'AntibodyComponentMigrationPreviewModal', element: 'root'})}
    >
      {showValueMappingView ? (
        <SourceFieldValueMappingView
          destinationFieldName={activeOptionMappingField}
          allowedOptions={activeMappingData.allowedOptions}
          sourceOptions={activeMappingData.sourceOptions}
          currentMappings={activeMappingData.currentMappings}
          onMappingChange={(sourceOptionId, destOptionName) => {
            handleOptionMappingChange(activeOptionMappingField, sourceOptionId, destOptionName);
          }}
          onBack={handleCloseOptionMapping}
        />
      ) : (
        <MigrationPreviewBody
          componentTypeName={componentTypeName}
          componentInterfaceId={componentInterfaceId}
          registryId={registryId}
          enforcedSchemaSpec={enforcedSchemaSpec}
          existingFields={existingFields}
          existingConstraint={existingConstraint}
          existingNamingStrategies={existingNamingStrategies}
          hasNameTemplate={hasNameTemplate}
          folderItemType={folderItemType}
          fieldMigrationMappings={fieldMigrationMappings}
          onMappingsChange={setFieldMigrationMappings}
          allRegistrySelectors={allRegistrySelectors}
          onOpenOptionMapping={handleOpenOptionMapping}
          isApplying={isApplying}
          onApply={onApply}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

export default function QueryingAntibodyComponentMigrationPreviewModal(
  props: Omit<AntibodyComponentMigrationPreviewModalProps, 'allRegistrySelectors'>
): JSX.Element {
  const result = useBenchlingQuery<SchemaFieldSelectorsForMigrationQuery>(
    SCHEMA_FIELD_SELECTORS_FOR_MIGRATION_QUERY
  );
  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <Modal
        title={`Apply ${props.componentTypeName} protein component type`}
        opened={true}
        onClose={props.onClose}
        size="lg"
        padding="lg"
      >
        {result.type === QueryResultType.ERROR ? (
          <Alert variant="error">
            An error occurred while loading schema fields. Refresh the page and try again.
          </Alert>
        ) : (
          <Center>
            <Loader />
          </Center>
        )}
      </Modal>
    );
  }
  const allRegistrySelectors = result.data.schemaFieldSelectors
    .filter((s): s is RegistrySchemaFieldSelector => s.__typename === 'SchemaFieldSelectorNode')
    .filter((s) => s.registry.id === props.registryId);
  return <AntibodyComponentMigrationPreviewModal {...props} allRegistrySelectors={allRegistrySelectors} />;
}
